import { create } from 'zustand';
import { API, API_BASE_URL } from '../lib/api';

const TIMELINE_LIMIT = 100;

const formatTimelineEvent = (event) => {
  const createdAt = event?.created_at ? new Date(event.created_at) : new Date();
  return {
    id: event?.id || event?.tempId || `tmp-${createdAt.getTime()}`,
    event_type: event?.event_type || event?.type || 'system',
    type: event?.event_type || event?.type || 'system',
    title: event?.title || 'Activity',
    meta: event?.meta || {},
    source: event?.meta?.source || event?.source,
    created_at: createdAt.toISOString(),
    timestamp: createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    error: event?.error
  };
};

// Tool alias mapping for backwards compatibility
// 2025-09 route taxonomy update:
// Canonical tool keys: flow, live, studio, diff, slices, gate
// Maintain backwards compatibility with previous naming variants.
const TOOL_ALIASES = { 
  overview: 'flow',
  guide: 'flow',
  home: 'flow',
  diffoscope: 'diff', 
  sliceHunter: 'slices',
  'slice-hunter': 'slices',
  slice: 'slices', // singular -> plural canonical
  'live-trial': 'live',
  'rag-studio': 'studio'
};

export function normalizeToolId(id) { 
  return TOOL_ALIASES[id] || id; 
}

/**
 * Shared studio state for tab management, layout, and context
 */
const useStudioStore = create((set, get) => ({
  // Workspace view state
  activeTool: 'live',
  setActiveTool: (toolName) => {
    const normalizedTool = normalizeToolId(toolName) || 'live';
    set({ activeTool: normalizedTool });
  },

  // Dashboard cards
  activeCards: ['live'],
  toggleCard: (toolName) => {
    const normalizedTool = normalizeToolId(toolName) || 'live';
    set((state) => {
      const exists = state.activeCards.includes(normalizedTool);
      const nextCards = exists
        ? state.activeCards.filter((card) => card !== normalizedTool)
        : [...state.activeCards, normalizedTool];
      return {
        activeCards: nextCards.length > 0 ? nextCards : ['live'],
        activeTool: nextCards.length > 0 ? nextCards[nextCards.length - 1] : 'live',
      };
    });
  },
  openCard: (toolName) => {
    const normalizedTool = normalizeToolId(toolName) || 'live';
    set((state) => {
      if (state.activeCards.includes(normalizedTool)) {
        return { activeTool: normalizedTool };
      }
      const nextCards = [...state.activeCards, normalizedTool];
      return { activeCards: nextCards, activeTool: normalizedTool };
    });
  },
  removeCard: (toolName) => {
    const normalizedTool = normalizeToolId(toolName) || 'live';
    set((state) => {
      const nextCards = state.activeCards.filter((card) => card !== normalizedTool);
      return {
        activeCards: nextCards.length > 0 ? nextCards : ['live'],
        activeTool: nextCards.length > 0 ? nextCards[nextCards.length - 1] : 'live',
      };
    });
  },
  setCards: (tools) => {
    const deduped = Array.from(new Set((tools || []).map(normalizeToolId))).filter(Boolean);
    const nextCards = deduped.length > 0 ? deduped : ['live'];
    set({ activeCards: nextCards, activeTool: nextCards[nextCards.length - 1] });
  },

  // Tab management
  activeTab: 0,
  setActiveTab: (tab) => set({ activeTab: tab }),
  
  // Layout management
  splitRatio: localStorage.getItem('studio-split-ratio') || '70-30',
  setSplitRatio: (ratio) => {
    localStorage.setItem('studio-split-ratio', ratio);
    set({ splitRatio: ratio });
  },
  
  // Context & History feed
  contextFeed: [],
  timelineLoaded: false,
  timelineError: '',
  timelineSupported: true,
  timelineNoticeShown: false,
  notifications: [],
  timelineStream: null,
  timelineRetryCount: 0,
  baselineRunId: typeof window !== 'undefined' ? localStorage.getItem('studio-baseline-run') : null,
  pushNotification: ({ id, severity = 'info', message, details } = {}) => {
    if (!message) return;
    const notificationId = id || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    set((state) => ({
      notifications: [
        { id: notificationId, severity, message, details },
        ...state.notifications.filter((item) => item.id !== notificationId)
      ].slice(0, 5)
    }));
  },
  dismissNotification: (id) => set((state) => ({
    notifications: state.notifications.filter((item) => item.id !== id)
  })),
  pushEvent: (event = {}) => {
    const now = new Date();
    const eventType = event.event_type || event.type || 'ui';
    const normalizedTitle = event.title || 'Activity logged';
    const meta = {
      ...(event.meta || {}),
      source: event.source || event.meta?.source || 'ui'
    };

    const tempEvent = formatTimelineEvent({
      id: `tmp-${now.getTime()}`,
      event_type: eventType,
      title: normalizedTitle,
      meta,
      created_at: now.toISOString()
    });

    set((state) => ({
      contextFeed: [tempEvent, ...state.contextFeed.filter((existing) => existing.id !== tempEvent.id)].slice(0, TIMELINE_LIMIT)
    }));

    if (!get().timelineSupported) {
      return;
    }

    API.system.recordTimeline({
      event_type: eventType,
      title: normalizedTitle,
      meta
    })
      .then((saved) => {
        const savedEvent = formatTimelineEvent(saved);
        set((state) => {
          const filtered = state.contextFeed.filter((existing) => existing.id !== tempEvent.id);
          return {
            contextFeed: [savedEvent, ...filtered].slice(0, TIMELINE_LIMIT),
            timelineSupported: true
          };
        });
      })
      .catch((error) => {
        if (error?.status === 404) {
          set((state) => {
            if (!state.timelineNoticeShown) {
              get().pushNotification({
                severity: 'info',
                message: 'Timeline service is not configured for this environment yet.',
              });
            }
            return {
              timelineSupported: false,
              timelineError: '',
              timelineNoticeShown: true,
              contextFeed: state.contextFeed.map((existing) =>
                existing.id === tempEvent.id ? { ...existing, error: undefined } : existing
              )
            };
          });
          return;
        }
        set((state) => ({
          contextFeed: state.contextFeed.map((existing) =>
            existing.id === tempEvent.id
              ? { ...existing, error: error?.message || 'Failed to persist event' }
              : existing
          )
        }));
      });
  },
  fetchTimeline: async (limit = TIMELINE_LIMIT, force = false) => {
    const state = get();
    if (!force && state.timelineLoaded && !state.timelineSupported) {
      return;
    }
    try {
      const events = await API.system.timeline(limit);
      set({
        contextFeed: events.map((event) => formatTimelineEvent(event)),
        timelineLoaded: true,
        timelineError: '',
        timelineSupported: true
      });
      get().startTimelineStream();
    } catch (error) {
      if (error?.status === 404) {
        set((state) => {
          if (!state.timelineNoticeShown) {
            get().pushNotification({
              severity: 'info',
              message: 'Timeline service is not configured for this environment yet.',
            });
          }
          return {
            timelineLoaded: true,
            timelineSupported: false,
            timelineError: '',
            timelineNoticeShown: true,
            contextFeed: state.contextFeed
          };
        });
        get().stopTimelineStream();
        return;
      }
      console.error('Timeline fetch failed:', error);
      set({ timelineLoaded: true, timelineError: error?.message || 'Unable to load timeline' });
      get().pushNotification({
        severity: 'error',
        message: error?.message || 'Timeline failed to load.',
      });
    }
  },

  startTimelineStream: () => {
    if (typeof window === 'undefined') {
      return;
    }
    const state = get();
    if (!state.timelineSupported || state.timelineStream) {
      return;
    }
    if (typeof EventSource === 'undefined') {
      return;
    }

    try {
      const stream = new EventSource(`${API_BASE_URL}/system/timeline/stream`);
      stream.onmessage = (event) => {
        // Reset retry count on successful message (connection restored)
        set({ timelineRetryCount: 0 });
        
        if (!event?.data) return;
        try {
          const payload = JSON.parse(event.data);
          const formatted = formatTimelineEvent(payload);
          set((current) => ({
            contextFeed: [formatted, ...current.contextFeed.filter((existing) => existing.id !== formatted.id)].slice(0, TIMELINE_LIMIT)
          }));
        } catch (err) {
          console.warn('Failed to parse timeline event:', err);
        }
      };
      stream.onerror = () => {
        stream.close();
        const currentState = get();
        set({ timelineStream: null, timelineRetryCount: currentState.timelineRetryCount + 1 });
        
        // Limit retries to prevent infinite loops
        const MAX_RETRIES = 3;
        if (currentState.timelineRetryCount < MAX_RETRIES) {
          get().pushNotification({
            severity: 'warning',
            message: `Timeline stream disconnected. Retrying in 15s... (${currentState.timelineRetryCount + 1}/${MAX_RETRIES})`,
          });
          window.setTimeout(() => {
            get().startTimelineStream();
          }, 15000);
        } else {
          get().pushNotification({
            severity: 'error',
            message: 'Timeline stream failed after maximum retries. Please refresh the page.',
          });
          set({ timelineSupported: false });
        }
      };
      set({ timelineStream: stream });
    } catch (err) {
      console.error('Failed to start timeline stream:', err);
      get().pushNotification({
        severity: 'error',
        message: 'Timeline stream could not be opened.',
      });
    }
  },

  stopTimelineStream: () => {
    const state = get();
    if (state.timelineStream) {
      state.timelineStream.close();
    }
    set({ timelineStream: null });
  },

  setBaselineRunId: (runId) => {
    if (typeof window !== 'undefined') {
      if (runId) {
        localStorage.setItem('studio-baseline-run', runId);
      } else {
        localStorage.removeItem('studio-baseline-run');
      }
    }
    set({ baselineRunId: runId || null });
  },

  clearFeed: () => set({ contextFeed: [] }),
  
  // Enhanced Cross-tool Context Sharing
  currentRun: null,
  setCurrentRun: (run) => {
    set({ currentRun: run });
    get().pushEvent({
      type: 'context-update',
      title: `Run context updated: ${run?.name || run?.id || 'Unknown'}`,
      meta: { type: 'run', data: run },
      source: 'system'
    });
  },
  
  currentSuite: null,
  setCurrentSuite: (suite) => {
    set({ currentSuite: suite });
    get().pushEvent({
      type: 'context-update', 
      title: `Suite context updated: ${suite?.name || suite?.id || 'Unknown'}`,
      meta: { type: 'suite', data: suite },
      source: 'system'
    });
  },
  
  currentVersion: null,
  setCurrentVersion: (version) => {
    set({ currentVersion: version });
    get().pushEvent({
      type: 'context-update',
      title: `Version context updated: ${version?.promptKey || 'Unknown'} v${version?.version || '?'}`,
      meta: { type: 'version', data: version },
      source: 'system'
    });
  },

  // Cross-tool workflow state
  workflowContext: {
    results: null,        // Results from Live Trial / RAG Studio
    comparison: null,     // Diff comparison data
    sliceAnalysis: null,  // Slice analysis data
    suggestions: [],      // Cross-tool suggestions
    agentJobs: [],
    recommendations: [],
  },
  
  setWorkflowResults: (results, source) => set((state) => {
    const newWorkflow = {
      ...state.workflowContext,
      results: {
        data: results,
        source: source, // 'liveTrial' | 'ragStudio'
        timestamp: new Date().toISOString(),
      }
    };
    
    get().pushEvent({
      type: 'workflow-update',
      title: `Results available from ${source}`,
      meta: { type: 'results', source, resultCount: results?.cases?.length || results?.metrics?.total_cases || 'Unknown' },
      source: source
    });
    
    return { workflowContext: newWorkflow };
  }),

  setAgentInsights: (payload, source = 'agents') => set((state) => {
    const newWorkflow = {
      ...state.workflowContext,
      agentJobs: payload?.jobs || [],
      recommendations: payload?.recommendations || [],
    };

    get().pushEvent({
      type: 'workflow-update',
      title: 'Agent insights refreshed',
      meta: { type: 'agents', jobs: newWorkflow.agentJobs.length, recommendations: newWorkflow.recommendations.length },
      source,
    });

    return { workflowContext: newWorkflow };
  }),

  setWorkflowComparison: (comparison) => set((state) => {
    const newWorkflow = {
      ...state.workflowContext,
      comparison: {
        data: comparison,
        timestamp: new Date().toISOString(),
      }
    };
    
    get().pushEvent({
      type: 'workflow-update',
      title: `Diff comparison completed`,
      meta: { type: 'comparison', changes: comparison?.hunks?.length || 0 },
      source: 'diff'
    });
    
    return { workflowContext: newWorkflow };
  }),
  
  setWorkflowSliceAnalysis: (analysis) => set((state) => {
    const newWorkflow = {
      ...state.workflowContext,
      sliceAnalysis: {
        data: analysis,
        timestamp: new Date().toISOString(),
      }
    };
    
    get().pushEvent({
      type: 'workflow-update',
      title: `Slice analysis completed`,
      meta: { type: 'slice', sliceCount: analysis?.slices?.length || 0 },
      source: 'slice'
    });
    
    return { workflowContext: newWorkflow };
  }),
  
  // Cross-tool navigation with context
  openToolWithContext: (toolName, contextData) => {
    get().openCard(toolName);
    get().navigateToTab(toolName);
    
    // Set relevant context based on what's being passed
    if (contextData?.run) get().setCurrentRun(contextData.run);
    if (contextData?.version) get().setCurrentVersion(contextData.version);
    if (contextData?.suite) get().setCurrentSuite(contextData.suite);
    
    get().pushEvent({
      type: 'navigation',
      title: `Opened ${toolName} with context`,
      meta: { tool: toolName, context: Object.keys(contextData || {}) },
      source: 'workflow'
    });
  },
  
  // Generate cross-tool suggestions based on current state
  generateSuggestions: () => {
    const state = get();
    const suggestions = [];

    const criticRecs = Array.isArray(state.workflowContext.recommendations)
      ? state.workflowContext.recommendations
      : [];
    criticRecs.forEach((rec, recIndex) => {
      const actions = Array.isArray(rec.suggested_actions) ? rec.suggested_actions : [];
      actions.forEach((action, actionIndex) => {
        if (!action?.tool) return;
        suggestions.push({
          id: `critic-${recIndex}-${actionIndex}`,
          title: action.title || 'Follow-up action',
          description: action.description || rec.summary || 'Review agent recommendation.',
          action: { type: 'openTool', tool: normalizeToolId(action.tool), context: { runId: rec.run_id } },
          priority: action.priority || 'medium'
        });
      });
    });

    // If we have results but no comparison
    if (state.workflowContext.results && !state.workflowContext.comparison) {
      suggestions.push({
        id: 'compare-versions',
        title: 'Compare with previous version',
        description: 'Use Diff tool to see what changed in your prompt',
        action: { type: 'openTool', tool: 'diff', context: { version: state.currentVersion } },
        priority: 'high'
      });
    }
    
    // If we have results but no slice analysis  
    if (state.workflowContext.results && !state.workflowContext.sliceAnalysis) {
      suggestions.push({
        id: 'analyze-slices',
        title: 'Analyze performance patterns',
        description: 'Use Slice tool to find patterns in your results',
        action: { type: 'openTool', tool: 'slice', context: { run: state.currentRun } },
        priority: 'high'
      });
    }
    
    // If we have comparison showing degradation
    if (state.workflowContext.comparison?.data?.cost_estimate?.delta_usd > 0.001) {
      suggestions.push({
        id: 'cost-analysis',
        title: 'Investigate cost increase',
        description: `Your changes increased cost by $${state.workflowContext.comparison.data.cost_estimate.delta_usd.toFixed(4)}`,
        action: { type: 'openTool', tool: 'slice', context: { comparison: state.workflowContext.comparison } },
        priority: 'medium'
      });
    }

    // If we have slice analysis showing concerning patterns
    const slices = state.workflowContext.sliceAnalysis?.data?.slices;
    if (Array.isArray(slices) && slices.some((slice) => Number(slice?.impact || 0) > 20)) {
      suggestions.push({
        id: 'investigate-performance',
        title: 'High-impact performance issues found',
        description: 'Some slices show significant performance degradation',
        action: { type: 'openTool', tool: 'diff', context: { version: state.currentVersion } },
        priority: 'high'
      });
    }

    const agentJobs = Array.isArray(state.workflowContext.agentJobs) ? state.workflowContext.agentJobs : [];
    const gateJob = agentJobs.find((job) => job.agent_type === 'gate');
    if (gateJob && gateJob.status === 'completed') {
      const result = gateJob.result || {};
      if (result.gate_passed && result.auto_action === 'approved') {
        suggestions.push({
          id: 'gate-approved',
          title: 'Version auto-approved',
          description: 'Quality gate passed automatically. Review and publish the approved prompt.',
          action: { type: 'openTool', tool: 'gate', context: { version: state.currentVersion } },
          priority: 'medium'
        });
      } else if (!result.gate_passed) {
        suggestions.push({
          id: 'gate-blocked',
          title: 'Gate blocked promotion',
          description: result.reason || 'Metrics fell below gating thresholds.',
          action: { type: 'openTool', tool: 'diff', context: { version: state.currentVersion } },
          priority: 'high'
        });
      }
    }

    // Always suggest quality gates if we have results
    if (state.workflowContext.results && state.currentVersion) {
      suggestions.push({
        id: 'quality-gate',
        title: 'Set up quality gates',
        description: 'Protect against regressions with automated quality gates',
        action: { type: 'openTool', tool: 'gate', context: { version: state.currentVersion, run: state.currentRun } },
        priority: 'low'
      });
    }

    const seen = new Set();
    return suggestions.filter((suggestion) => {
      if (seen.has(suggestion.id)) {
        return false;
      }
      seen.add(suggestion.id);
      return true;
    });
  },

  // Navigation helpers - now router-aware
  navigateToTab: (tabName) => {
    const normalizedTabName = normalizeToolId(tabName) || 'live';

    if (typeof window !== 'undefined') {
      const currentHash = window.location.hash || '#/workspace';
      const [, hashQueryRaw] = currentHash.replace(/^#/, '').split('?');
      const hashPath = '#/workspace';
      const searchParams = new URLSearchParams(hashQueryRaw || '');
      searchParams.set('tool', normalizedTabName);

      const nextHash = `${hashPath}?${searchParams.toString()}`;

      if (window.location.hash !== nextHash) {
        window.location.hash = nextHash;
      } else {
        window.dispatchEvent(new Event('hashchange'));
      }
    }

    set({ activeTool: normalizedTabName });
  },

  // Initialize from URL params (now handled by AppShell router logic)
  initializeFromURL: () => {
    // Router now handles URL initialization, but keep for backwards compatibility
    // This can be called by components that expect the old behavior
  }
}));

export default useStudioStore;
